import axiosInstance from '$api/index';
import {ApiRoute} from '$constants/api';
import store from '$redux/store';

export interface MonthlyRevenue {
  total_revenue: number;
  payment_total: number;
  insurance_covered: number;
  current_month: string;
}

export interface OutstandingPayments {
  unpaid_invoice_count: number;
  total_outstanding: number;
}

export interface OutstandingInsurance {
  unpaid_insurance_count: number;
  total_outstanding: number;
}

export interface PreferredPaymentMethodDetail {
  count: number;
  percentage: number;
}

export interface PreferredPaymentMethods {
  card: PreferredPaymentMethodDetail;
  cheque: PreferredPaymentMethodDetail;
  cash: PreferredPaymentMethodDetail;
  other: PreferredPaymentMethodDetail;
}

export interface TopInsuranceCompany {
  company_name: string;
  total_claimed: number;
  open_claims: number;
}

export interface InsuranceClaimStatus {
  count: number;
  percentage: number;
}

export interface InsuranceClaimsManagement {
  open: InsuranceClaimStatus;
  closed: InsuranceClaimStatus;
  rejected: InsuranceClaimStatus;
}

export interface DashboardData {
  monthly_revenue: MonthlyRevenue;
  outstanding_payments: OutstandingPayments;
  preferred_payment_methods: PreferredPaymentMethods;
  top_insurance_companies: TopInsuranceCompany[];
  outstanding_insurance_amounts: OutstandingInsurance;
  insurance_claims_management: InsuranceClaimsManagement;
}

export interface DashboardDataResponse {
  success: boolean;
  data: DashboardData;
  message: string;
}

export interface MonthlyRevenueApiResponse {
  success: boolean;
  data: {
    chart_data: MonthlyRevenueApiItem[];
    y_axis_ticks: number[];
  };
  message: string;
}

export interface MonthlyRevenueApiItem {
  month: string;
  booking_rate: number;
  revenue: number;
  estimated_revenue: number;
}

export async function getDashboardData(): Promise<DashboardDataResponse> {
  const token = store.getState().auth.token;
  const response = await axiosInstance.get(ApiRoute.Dashboard, {
    headers: {
      Authorization: token,
    },
  });
  return response.data;
}

export interface DownloadReportParams {
  data_options: string[];
  from_date: string;
  to_date: string;
  format: string;
}

export async function downloadDashboardReport(
  params: DownloadReportParams
): Promise<Blob> {
  const token = store.getState().auth.token;
  const response = await axiosInstance.get(ApiRoute.DashboardDownload, {
    params: {
      data_options: params.data_options.join(','),
      from_date: params.from_date,
      to_date: params.to_date,
      format: params.format,
    },
    headers: {
      Authorization: token,
    },
    responseType: 'blob',
  });
  return response.data;
}

export async function getMonthlyRevenueData(): Promise<MonthlyRevenueApiResponse> {
  const token = store.getState().auth.token;
  const response = await axiosInstance.get(ApiRoute.ExpadedMonthlyRevenue, {
    headers: {Authorization: token},
  });
  return response.data;
}

export async function downloadMonthlyRevenueReport(
  date: string,
  format: string = 'pdf'
): Promise<Blob> {
  const token = store.getState().auth.token;
  const response = await axiosInstance.get(ApiRoute.MonthlyRevenueDownload, {
    params: {
      date,
      format,
    },
    headers: {
      Authorization: token,
    },
    responseType: 'blob',
  });
  return response.data;
}
